/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (audit 3): a connection to someone else's SFU (to receive the media
 * of the people who publish there) fails without a word: the call goes on
 * and those people stay black and silent. Our own connection is left out:
 * its failure ends the call and is reported by the error page.
 */

import { filter, map, merge, type Observable, switchMap } from "rxjs";

import { type Behavior } from "../state/Behavior";
import { type ObservableScope } from "../state/ObservableScope";
import { type Graylog } from "../logging/Graylog";
import {
  type Connection,
  type ConnectionState,
} from "../state/CallViewModel/remoteMembers/Connection";

/** Sends the failure of each remote connection to Graylog, once. */
export function watchRemoteConnectionFailures(
  scope: ObservableScope,
  connections$: Observable<{ getConnections(): Connection[] }>,
  localConnection$: Behavior<Connection | null>,
  graylog: Graylog,
): void {
  const reported = new WeakSet<Connection>();
  connections$
    .pipe(
      switchMap((data) =>
        merge(
          ...data.getConnections().map((connection) =>
            connection.state$.pipe(
              filter(
                (state: ConnectionState | Error): state is Error =>
                  state instanceof Error,
              ),
              map((error) => ({ connection, error })),
            ),
          ),
        ),
      ),
      scope.bind(),
    )
    .subscribe(({ connection, error }) => {
      if (connection === localConnection$.value || reported.has(connection))
        return;
      reported.add(connection);
      graylog.logWarning("Remote SFU connection failed", {
        serviceUrl: connection.transport.livekit_service_url,
        error: error.message,
      });
    });
}
